import { useMemo, useState } from 'react'
import { ArrowRight, Search } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { CustomSelect } from '~/components/ui/custom-select'
import type { AssessmentResponse } from '~/api/types'
import {
    ASSESSMENT_TYPE_OPTIONS,
    CLASSIFICATION_OPTIONS,
} from '~/components/assessment/labels'
import { AssessmentOptionCard } from './assessment-option-card'
import { MaintenanceStepIndicator } from './maintenance-step-indicator'

const ALL = 'all'

const SCALE_FILTER_OPTIONS = [
    { value: ALL, label: '규모 전체' },
    { value: '일반', label: '일반' },
    { value: '소규모', label: '소규모' },
]

const CLASSIFICATION_FILTER_OPTIONS = [
    { value: ALL, label: '분류 전체' },
    ...CLASSIFICATION_OPTIONS.map((value) => ({ value, label: value })),
]

const TYPE_FILTER_OPTIONS = [
    { value: ALL, label: '단계 전체' },
    ...ASSESSMENT_TYPE_OPTIONS.map((value) => ({ value, label: value })),
]

export function AssessmentSelectionStep({
    assessments,
    selectedId,
    onSelect,
    onStart,
}: {
    assessments: AssessmentResponse[]
    selectedId: string
    onSelect(value: string): void
    onStart(value?: string): void
}) {
    const [query, setQuery] = useState('')
    const [scale, setScale] = useState(ALL)
    const [classification, setClassification] = useState(ALL)
    const [assessmentType, setAssessmentType] = useState(ALL)

    const filtered = useMemo(() => {
        const keyword = query.trim().toLowerCase()
        return assessments.filter((assessment) => {
            if (keyword) {
                const haystack = [assessment.title, assessment.industry ?? '']
                    .join(' ')
                    .toLowerCase()
                if (!haystack.includes(keyword)) return false
            }
            if (scale !== ALL && assessment.scale !== scale) return false
            if (
                classification !== ALL &&
                assessment.classification !== classification
            )
                return false
            if (
                assessmentType !== ALL &&
                assessment.assessmentType !== assessmentType
            )
                return false
            return true
        })
    }, [assessments, query, scale, classification, assessmentType])

    return (
        <section
            className='min-h-0 min-w-0 flex-1 overflow-y-auto'
            aria-label='대상 평가 선택'>
            <div className='mx-auto grid w-full max-w-[1280px] min-w-0 gap-5 pb-8'>
                <MaintenanceStepIndicator />

                <div className='flex flex-wrap items-end justify-between gap-3'>
                    <div>
                        <p className='text-muted-foreground mt-2 max-w-3xl text-sm'>
                            변경 영향을 확인할 평가를 선택하세요. 선택한 평가의
                            체크리스트, 답변, 채점 결과를 기준으로 분석합니다.
                        </p>
                    </div>
                    <Button
                        type='button'
                        onClick={() => onStart()}
                        disabled={!selectedId}
                        className='rounded-sm'>
                        다음 단계
                        <ArrowRight />
                    </Button>
                </div>

                <div className='flex flex-wrap items-center gap-2'>
                    <label className='relative block min-w-0 flex-1 sm:max-w-72'>
                        <span className='sr-only'>평가 검색</span>
                        <Search
                            className='text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2'
                            aria-hidden='true'
                        />
                        <Input
                            type='search'
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder='평가명·업종 검색'
                            className='h-9 pl-9'
                        />
                    </label>
                    <CustomSelect
                        value={scale}
                        options={SCALE_FILTER_OPTIONS}
                        onValueChange={setScale}
                        ariaLabel='규모 필터'
                        containerClassName='w-32'
                        className='h-9'
                    />
                    <CustomSelect
                        value={classification}
                        options={CLASSIFICATION_FILTER_OPTIONS}
                        onValueChange={setClassification}
                        ariaLabel='분류 필터'
                        containerClassName='w-36'
                        className='h-9'
                    />
                    <CustomSelect
                        value={assessmentType}
                        options={TYPE_FILTER_OPTIONS}
                        onValueChange={setAssessmentType}
                        ariaLabel='단계 필터'
                        containerClassName='w-32'
                        className='h-9'
                    />
                </div>

                {filtered.length > 0 ? (
                    <div className='grid gap-3 lg:grid-cols-2'>
                        {filtered.map((assessment) => (
                            <AssessmentOptionCard
                                key={assessment.id}
                                assessment={assessment}
                                selected={assessment.id === selectedId}
                                onSelect={(value) => {
                                    onSelect(value)
                                    onStart(value)
                                }}
                            />
                        ))}
                    </div>
                ) : (
                    <div className='border-border text-muted-foreground rounded-md border border-dashed p-8 text-center text-sm'>
                        조건에 맞는 평가가 없어요.
                    </div>
                )}
            </div>
        </section>
    )
}
